<template>
  <div class="price col mt-lg-0">
    <div class="mb-5">
      <step-title
        title="Cadastrar Oferta"
        description="Cadastre pelo menos uma oferta para completar o cadastro do seu curso
        online. Você pode cadastrar quantas ofertas desejar a qualquer momento no
        gerenciamento do produto."
      />
    </div>

    <bp-input
      v-model="form.name"
      id="name"
      class="pt-6"
      label="Nome do preço"
      placeholder="Ex: Promoção de Natal"
      :errors="errors.name"
      description="Este nome será exibido apenas para você, em sua tabela de preços."
      required
    />

    <bp-input
      class="pt-6"
      v-model="form.description"
      id="description"
      label="Descrição no checkout"
      placeholder="Insira uma descrição para a sua página de pagamentos"
      :errors="errors.description"
      description="Atenção! Esta descrição aparecerá para seus compradores."
      textarea
      :limit="980"
      rows="5"
      required
    />
    <hr />
    <template>
      <h3>Definir período da oferta</h3>
      <div class="row">
        <div class="col-lg-6">
          <form-group
            v-model="form.start_at"
            is-date
            readonly
            required
            id="start_at"
            mode="dateTime"
            withClearButton
            class="mt-6 mt-lg-5"
            mask="##/##/#### ##:##"
            label="Início da oferta"
            placeholder="Selecione a data incial..."
            :minDate="new Date()"
            :errors="errors.start_at"
          />
          <span class="text-secondary fs-14">
            Escolha a data a partir da qual a oferta ficará disponível
          </span>
        </div>

        <div class="col-lg-6">
          <form-group
            v-model="form.end_at"
            is-date
            readonly
            id="end_at"
            mode="dateTime"
            withClearButton
            class="mt-6 mt-lg-5"
            label="Fim da oferta"
            mask="##/##/#### ##:##"
            placeholder="Selecione a data final..."
            :errors="errors.end_at"
            :disabled="!form.start_at"
            :minDate="transformDate(form.start_at)"
          />
          <span class="text-secondary fs-14">
            Escolha a data a partir da qual a oferta ficará indisponível
          </span>
        </div>
      </div>

      <bp-input
        v-model="form.expire_url"
        class="mt-6 mt-lg-5"
        id="expire_url"
        label="URL para redirecionar após oferta expirar"
        placeholder="Ex: http://www.meusite.com.br/oferta-expirou"
        :errors="errors.expire_url"
      />

      <div class="d-flex align-items-center pt-6">
        <b-form-checkbox
          switch
          class="mr-5"
          id="is_stock"
          v-model="form.is_stock"
          :class="{ 'text-primary': form.is_stock }"
        >
          Ativar/Desativar estoque
        </b-form-checkbox>
      </div>

      <div v-if="form.is_stock">
        <bp-input
          v-model="form.stock"
          class="mt-6 mt-lg-5"
          id="stock"
          label="Quantidade em estoque"
          type="tel"
          mask="##########"
          placeholder="0"
          :errors="errors.stock"
        />
        <bp-input
          v-model="form.stock_empty_url"
          class="mt-6 mt-lg-5"
          id="stock_empty_url"
          label="URL para redirecionar após estoque acabar"
          placeholder="Ex: http://www.meusite.com.br/estoque-acabou"
          :errors="errors.stock_empty_url"
        />
      </div>
    </template>
    <hr />
    <h3 class="pb-5">Precificação da oferta</h3>
    <bp-input
      v-model="form.price"
      money
      :moneyConfig="moneyConfig"
      class="my-5"
      id="price"
      preIcon="fa-light fa-brazilian-real-sign"
      label="Valor em Real Brasileiro (R$)"
      placeholder="Informe o preço"
      required
      :errors="errors.price"
      description="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que R$ 4,00."
      :min="4"
    />
    <bp-input
      :disabled="!showForeign"
      :info="getInfo('USD')"
      v-model="form.price_usd"
      money
      :moneyConfig="{ ...moneyConfig, currency: '$' }"
      class="my-5"
      id="price"
      preIcon="fa-light fa-dollar"
      label="Valor em Dólar Americano (US$)"
      placeholder="Informe o preço"
      :errors="errors.price_usd"
      description="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que US$ 4,00."
      :min="4"
    />
    <bp-input
      :disabled="!showForeign"
      :info="getInfo('EUR')"
      v-model="form.price_eur"
      money
      :moneyConfig="{ ...moneyConfig, currency: '€' }"
      class="my-5"
      id="price"
      preIcon="fa-light fa-euro"
      label="Valor em Euro (€)"
      placeholder="Informe o preço"
      :errors="errors.price_eur"
      description="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que € 4,00."
      :min="4"
    />

    <hr />
    <h3>Origem das vendas</h3>

    <bp-input
      class="mt-5"
      v-model="form.sck"
      id="sck"
      label="SCK"
      placeholder="Digite o parâmetro desejado"
      description="Esse parâmetro te ajudará a identificar a origem da compra."
    />

    <hr />
    <h3>Configurar Order Bump</h3>
    <span class="text-neutral-400">
      Adicione um ou mais Order Bump dentro desta oferta
    </span>

    <action-dispatcher action="product/get" :parameters="{ limit: 1000 }">
      <order-bump-add
        v-for="(order_bump, i) in form.order_bumps"
        class="mt-6 mb-7"
        :counter="i + 1"
        :form="order_bump"
        :key="i"
        @remove="item => form.order_bumps.splice(item - 1, 1)"
      />
    </action-dispatcher>

    <div class="d-flex gap-5">
      <bp-button
        variant="outlined"
        class="text-primary-700 my-6"
        label="Novo Order Bump"
        icon="fa-light fa-plus"
        size="md"
        @click="form.order_bumps.push({})"
      />

      <bp-button
        :disabled="!isValid"
        @click="saveOffer"
        variant="outlined"
        label="Salvar oferta"
        class="my-6"
        size="md"
      />
    </div>

    <hr class="my-0" />

    <div class="my-5">
      <offers-or-plans-container
        :isSignature="false"
        :items="offers"
        @delete="i => onRemove(i)"
        @edit="onEdit"
      />
    </div>
  </div>
</template>

<script>
import OffersOrPlansContainer from "@/components/product/OffersOrPlansContainer";
import OrderBumpAdd from "@/components/product/order-bump/OrderBumpAdd";
import { formFields, toComputerDate } from "@/functions/helpers.js";
import moment from "moment";
import { mapState } from "vuex";
const fields = [
  "id",
  "name",
  "description",
  "price",
  "currency",
  "is_main",
  "status",
  "start_at",
  "end_at",
  "order_bump",
  "sck",
  "expire_url",
  "is_stock",
  "stock",
  "stock_empty_url",
  "order_bumps"
];

export default {
  components: {
    OffersOrPlansContainer,
    OrderBumpAdd
  },
  props: {
    productCurrency: {
      type: String,
      default: ""
    },
    priceToEditIndex: {
      type: Number,
      default: null
    },
    currentProduct: {
      type: Object,
      default: () => {}
    },
    formErrors: {
      type: Object,
      default: () => ({})
    },
    showLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    let offer;
    return {
      loading: false,
      offer,
      countOrderBumps: 1,
      modalDeleteVisible: false,
      moneyConfig: {
        decimal: ",",
        thousands: ".",
        precision: 2,
        prefix: "",
        masked: false
      },
      offers: [],
      ...formFields(fields, {
        order_bumps: [{}],
        status: "active",
        currency: "BRL",
        is_main: false, // to control the submit of default and other offers
        is_stock: false,
        ...offer
      })
    };
  },
  watch: {
    offers(newValue) {
      this.$emit("hasOffer", !!newValue.length);
    }
  },
  computed: {
    ...mapState("product", ["products", "paymentMethods"]),
    ...mapState("offer", ["orderBumpOffers"]),
    ...mapState("sale", ["installments"]),
    isValid() {
      return (
        this.form.name &&
        this.form.description &&
        this.form.description.length < 980 &&
        this.form.start_at &&
        this.form.price &&
        this.form.price >= 4 &&
        (!parseFloat(this.form.price_eur) || this.form.price_eur >= 4) &&
        (!parseFloat(this.form.price_usd) || this.form.price_usd >= 4) &&
        this.form.currency &&
        !this.form.order_bumps.some(
          order_bump => !!order_bump.product_id && !order_bump.offer_id
        )
        // && this.form.payment_methods_ids.length
      );
    },
    showForeign() {
      return this.currentProduct?.payment_methods_ids?.includes(1);
    }
  },
  methods: {
    getInfo(currency) {
      if (this.showForeign) return "";
      const currencyName = {
        USD: "dólar americano",
        EUR: "euro"
      };
      return `Para habilitar configuração de preço em ${currencyName[currency]} seu checkout precisa aceitar Cartão de Crédito.`;
    },
    saveOffer() {
      let form = {
        ...this.form,
        status: "active",
        start_at: this.form.start_at
          ? toComputerDate(this.form.start_at)
          : null,
        end_at: this.form.end_at ? toComputerDate(this.form.end_at) : null,
        order_bumps: this.form.order_bumps.filter(ob => !!ob.offer_id)
      };

      if (Number.isInteger(this.indexToEdit)) {
        const aux = [...this.offers];

        aux.splice(this.indexToEdit, 1, form);
        this.offers = aux;
      } else {
        const existOffer = this.offers.find(p => p.name == this.form.name);

        if (existOffer) {
          this.errors.name = ["Já existe uma oferta com esse nome"];
          window.scrollTo(0, 0);
          return;
        }
        if (this.form.price < 4) {
          this.errors.price = ["O valor não pode ser menor que R$ 4,00"];
          return;
        }

        if (!this.orderBumpProduct) {
          form.order_bump = null;
        }
        this.offers = [...this.offers, form];
      }
      this.clear();
    },
    transformDate(date) {
      return moment(date, "DD/MM/YYYY hh:mm").toDate();
    },
    clear() {
      const { form, errors } = formFields(fields, {
        currency: "BRL",
        status: "active",
        installments: 1,
        is_main: false, // to control the submit of default and other offers
        is_stock: false,
        order_bumps: [{}]
      });
      this.form = form;
      this.errors = errors;
      this.indexToEdit = null;
    },
    onEdit(index) {
      this.form = {
        ...this.offers[index],
        start_at: moment(this.offers[index].start_at).format("DD/MM/YYYY hh:mm")
      };
      if (this.form.end_at) {
        this.form.end_at = moment(this.form.end_at).format("DD/MM/YYYY hh:mm");
      }
      this.indexToEdit = index;
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    },
    onRemove(index) {
      this.offers = this.offers.filter((_, i) => i !== index);
      this.clear();
    }
  }
};
</script>

<style lang="scss" scoped>
.price {
  ::v-deep.form-group.is-date {
    padding: 8px 0 0;
    input.form-control {
      border: 1px solid #a9a9a9;
      &::placeholder {
        font-size: 16px !important;
      }
    }
    label {
      font-size: 14px;
    }
  }
  .custom-control.custom-switch {
    z-index: 0 !important;
  }
}
</style>
